import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { BASE_SCHEMA } from "../../cols";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  useCreateItp,
  useDeleteItp,
  useGetItps,
  useUpdateItp,
} from "../../hooks";
import { useSendToInspectionPlan } from "./useSendToInspectionPlan";

const EMPTY_BACKEND_DATE_VALUES = new Set([
  "1899-11-30",
  "1899-11-30T00:00:00.000Z",
  "0001-01-01",
  "0001-01-01T00:00:00.000Z",
]);

function UploadCell({ row, columnKey, accept, onFileSelected }) {
  const fileMeta = row.original?.[columnKey];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
      }}
    >
      <IconButton component="label" size="small" sx={{ p: 0.25 }}>
        <CloudUploadIcon fontSize="small" />
        <input
          hidden
          type="file"
          accept={accept || "*/*"}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            onFileSelected(row.original.id, columnKey, file);
            e.target.value = "";
          }}
        />
      </IconButton>

      {fileMeta?.name ? (
        <Tooltip title={fileMeta.name}>
          <InsertDriveFileIcon fontSize="small" />
        </Tooltip>
      ) : null}
    </Box>
  );
}

const slugifyKey = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w]+/g, "_")
    .replace(/^_+|_+$/g, "") || `col_${Date.now()}`;

const uniqueStrings = (arr = []) => [
  ...new Set(arr.filter((v) => String(v ?? "").trim() !== "").map(String)),
];

const humanizeKey = (key) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const FIXED_HEADER_TO_CANONICAL_KEY = {
  "INSPECTION/TEST ACTIVITY": "inspectionTestActivities",
  "CONTROLLING DOCUMENT": "controllingDoc",
  "RES. PARTY/LOC": "resPartyLoc",
  "RECORDING DOCUMENT": "recordingDoc",
};

const FIXED_KEY_ALIASES = {
  inspectionTestActivities: [
    "inspectionTestActivities",
    "inspectionTestActivity",
    "inspection_test_activities",
    "inspection_test_activity",
  ],
  controllingDoc: [
    "controllingDoc",
    "controllingDocument",
    "controlling_document",
  ],
  resPartyLoc: ["resPartyLoc", "resPartyLocation", "res_party_loc", "resParty"],
  recordingDoc: ["recordingDoc", "recordingDocument", "recording_document"],
};

const normalizeHeaderLabel = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

const resolveCanonicalFixedKey = (column) => {
  const byHeader =
    FIXED_HEADER_TO_CANONICAL_KEY[normalizeHeaderLabel(column?.header)];
  if (byHeader) return byHeader;

  const rawKey = String(column?.key || "").trim();

  for (const [canonicalKey, aliases] of Object.entries(FIXED_KEY_ALIASES)) {
    if (aliases.includes(rawKey)) {
      return canonicalKey;
    }
  }

  return column?.key;
};

const normalizeSchemaColumn = (column) => {
  const canonicalKey = resolveCanonicalFixedKey(column);

  return {
    ...column,
    key: canonicalKey,
  };
};

const NORMALIZED_BASE_SCHEMA = BASE_SCHEMA.map(normalizeSchemaColumn);

const isPlaceholderDate = (value) => {
  if (!value) return true;
  return EMPTY_BACKEND_DATE_VALUES.has(String(value));
};

const normalizeBackendDateForInput = (value) => {
  if (!value || isPlaceholderDate(value)) return "";
  const stringValue = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return stringValue;
  }

  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const normalizeNullableString = (value) => {
  if (value === null || value === undefined) return "";
  return value;
};

const formatDateDD_MMMM_YYYY = (value) => {
  if (!value || isPlaceholderDate(value)) return "";

  const normalized = normalizeBackendDateForInput(value);
  if (!normalized) return "";

  const date = new Date(`${normalized}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const normalizeSpecEntry = (entry, fallbackKey = "") => {
  if (entry === null || entry === undefined) return null;

  const rawKey = entry.key || fallbackKey || entry.optionsKey || entry.header;
  const key = slugifyKey(rawKey);
  const optionsKey = entry.optionsKey || key;

  return {
    key,
    header: entry.header || humanizeKey(key),
    value: entry.value !== undefined && entry.value !== null ? entry.value : "",
    fieldType: entry.fieldType || "select",
    optionsKey,
    options: uniqueStrings(entry.options || []),
    group: entry.group || undefined,
  };
};

const extractSpecEntries = (specs) => {
  if (!specs) return [];

  if (Array.isArray(specs)) {
    return specs.map((entry) => normalizeSpecEntry(entry)).filter(Boolean);
  }

  if (typeof specs === "object") {
    return Object.entries(specs)
      .map(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          return normalizeSpecEntry({ ...value, key }, key);
        }

        return normalizeSpecEntry(
          {
            key,
            header: humanizeKey(key),
            value,
            fieldType: "select",
            optionsKey: key,
            options: value ? [value] : [],
          },
          key,
        );
      })
      .filter(Boolean);
  }

  return [];
};

const mergeColumns = (...groups) => {
  const map = new Map();

  groups
    .flat()
    .filter(Boolean)
    .forEach((column) => {
      if (!column?.key) return;

      if (!map.has(column.key)) {
        map.set(column.key, {
          ...column,
          optionsKey: column.optionsKey || column.key,
        });
        return;
      }

      map.set(column.key, {
        ...map.get(column.key),
        ...column,
        optionsKey:
          column.optionsKey || map.get(column.key)?.optionsKey || column.key,
      });
    });

  return [...map.values()];
};

const mapApiRowToUiRow = (item, fileMetaByRowId = {}) => {
  const specEntries = extractSpecEntries(item?.specs);

  const dynamicValues = specEntries.reduce((acc, spec) => {
    acc[spec.key] = normalizeNullableString(spec.value);
    return acc;
  }, {});

  return {
    ...item,
    inspectionTestActivities: normalizeNullableString(
      item?.inspectionTestActivities,
    ),
    rev: normalizeNullableString(item?.rev),
    procedureNo: normalizeNullableString(item?.procedureNo),
    specificInstruction: normalizeNullableString(item?.specificInstruction),
    controllingDoc: normalizeNullableString(item?.controllingDoc),
    resPartyLoc: normalizeNullableString(item?.resPartyLoc),
    acceptanceCriteria: normalizeNullableString(item?.acceptanceCriteria),
    recordingDoc: normalizeNullableString(item?.recordingDoc),
    startDate: normalizeBackendDateForInput(item?.startDate),
    endDate: normalizeBackendDateForInput(item?.endDate),
    progress: item?.progress ?? "",
    actualHours: item?.actualHours ?? "",
    link: normalizeNullableString(item?.link),

    shopDate: normalizeBackendDateForInput(item?.representativeDate),
    shopType: normalizeNullableString(item?.representativeFunc),
    shopSign: normalizeNullableString(item?.representativeInitials),

    clientDate: normalizeBackendDateForInput(item?.cdtDate),
    clientType: normalizeNullableString(item?.cdtFunc),
    clientSign: normalizeNullableString(item?.cdtInitials),

    inspectorDate: normalizeBackendDateForInput(item?.authorizedInspectionDate),
    inspectorType: normalizeNullableString(item?.authorizedInspectionFunc),
    inspectorSign: normalizeNullableString(item?.authorizedInspectionInitials),

    ...dynamicValues,
    ...(fileMetaByRowId[item?.id] || {}),
  };
};

const buildDynamicArtifactsFromApiRows = (rows = []) => {
  const columnsMap = new Map();

  rows.forEach((row) => {
    extractSpecEntries(row?.specs).forEach((spec) => {
      const existing = columnsMap.get(spec.key);

      if (!existing) {
        columnsMap.set(spec.key, {
          key: spec.key,
          header: spec.header,
          fieldType: spec.fieldType || "select",
          optionsKey: spec.optionsKey || spec.key,
          size: 160,
          options: uniqueStrings([...(spec.options || []), spec.value]),
        });
        return;
      }

      columnsMap.set(spec.key, {
        ...existing,
        header: existing.header || spec.header,
        fieldType: existing.fieldType || spec.fieldType || "select",
        optionsKey: existing.optionsKey || spec.optionsKey || spec.key,
        options: uniqueStrings([
          ...(existing.options || []),
          ...(spec.options || []),
          spec.value,
        ]),
      });
    });
  });

  const columns = [...columnsMap.values()].map(({ options, ...rest }) => rest);
  const optionsMap = Object.fromEntries(
    [...columnsMap.values()].map((col) => [col.optionsKey, col.options || []]),
  );

  return { columns, optionsMap };
};

const toNullableDate = (value) => {
  const normalized = normalizeBackendDateForInput(value);
  return normalized || null;
};

function buildMrtColumns(
  schemaColumns,
  selectedType,
  optionsByTypeState,
  onFileSelected,
) {
  const typeOpts = optionsByTypeState[selectedType] || {};

  const toSelectProps = (optionsKey, currentValue) => {
    const opts = uniqueStrings([
      ...(Array.isArray(typeOpts[optionsKey]) ? typeOpts[optionsKey] : []),
      currentValue,
    ]);

    return {
      select: true,
      children: opts.map((v) => (
        <MenuItem key={v} value={v}>
          {v}
        </MenuItem>
      )),
    };
  };

  const mapCol = (c) => {
    const col = {
      accessorKey: c.key,
      header: c.header,
      size: c.size,
    };

    if (c.fieldType === "icon") {
      return {
        accessorKey: c.key,
        header: "",
        Header: c.Header,
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        size: c.size ?? 44,
        Cell: ({ row }) => (
          <UploadCell
            row={row}
            columnKey={c.key}
            accept={c.accept}
            onFileSelected={onFileSelected}
          />
        ),
      };
    }

    if (c.fieldType === "date") {
      col.muiEditTextFieldProps = { type: "date" };
      col.Cell = ({ cell }) => (
        <span>{formatDateDD_MMMM_YYYY(cell.getValue())}</span>
      );
    } else if (c.fieldType === "select") {
      col.muiEditTextFieldProps = ({ cell }) =>
        toSelectProps(c.optionsKey, cell?.getValue?.());
    } else {
      col.muiEditTextFieldProps = { type: "text" };
    }

    return col;
  };

  const normal = schemaColumns.filter((c) => !c.group);
  const shop = schemaColumns.filter((c) => c.group === "SHOP");
  const client = schemaColumns.filter((c) => c.group === "CLIENT");
  const inspector = schemaColumns.filter(
    (c) => c.group === "AUTHORIZED-INSPECTOR",
  );

  return [
    ...normal.map(mapCol),
    ...(inspector.length
      ? [
          {
            id: "inspectorGroup",
            header: "AUTHORIZED-INSPECTOR",
            columns: inspector.map(mapCol),
          },
        ]
      : []),
    ...(shop.length
      ? [{ id: "shopGroup", header: "SHOP", columns: shop.map(mapCol) }]
      : []),
    ...(client.length
      ? [{ id: "clientGroup", header: "CLIENT", columns: client.map(mapCol) }]
      : []),
  ];
}

function AddColumnDialog({ open, onClose, onAdd }) {
  const [header, setHeader] = React.useState("");

  React.useEffect(() => {
    if (open) setHeader("");
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Add Column (Select by default)
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <TextField
          label="Column Header"
          value={header}
          onChange={(e) => setHeader(e.target.value)}
          fullWidth
          size="small"
        />
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "text.secondary" }}
        >
          Column will be saved inside specs and rendered again after fetch.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            if (!header.trim()) return;
            onAdd(header.trim());
            onClose();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddOptionDialog({ open, onClose, onAdd, columnHeader }) {
  const [val, setVal] = React.useState("");

  React.useEffect(() => {
    if (open) setVal("");
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Add Option — {columnHeader}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <TextField
          label="New option value"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          fullWidth
          size="small"
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            const v = val.trim();
            if (!v) return;
            onAdd(v);
            onClose();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ITPRowModal({
  open,
  mode,
  schemaColumns,
  selectedType,
  optionsByTypeState,
  onAddColumn,
  onAddOption,
  initialValues,
  onCancel,
  onSave,
  isSaving,
}) {
  const [form, setForm] = React.useState({});
  const [addColOpen, setAddColOpen] = React.useState(false);
  const [addOptOpen, setAddOptOpen] = React.useState(false);
  const [activeOpt, setActiveOpt] = React.useState(null);

  const visibleColumns = React.useMemo(
    () => schemaColumns.filter((c) => c.fieldType !== "icon"),
    [schemaColumns],
  );

  React.useEffect(() => {
    if (open) {
      const next = {};
      visibleColumns.forEach((c) => {
        next[c.key] = initialValues?.[c.key] ?? "";
      });
      setForm(next);
    }
  }, [open, visibleColumns, initialValues]);

  const typeOpts = optionsByTypeState[selectedType] || {};

  const getFieldSpan = (c) => {
    const key = c.key;

    const smallKeys = new Set([
      "number",
      "rev",
      "progress",
      "actualHrs",
      "actualHours",
      "shopType",
      "clientType",
      "inspectorType",
      "shopSign",
      "clientSign",
      "inspectorSign",
      "shopDate",
      "clientDate",
      "inspectorDate",
      "startDate",
      "endDate",
    ]);

    const isDate = c.fieldType === "date";

    const isSmall =
      smallKeys.has(key) ||
      isDate ||
      /type|sign|date|rev|no|progress|hour/i.test(key);

    if (isSmall) {
      return { xs: 12, sm: 6, md: 2 };
    }

    return { xs: 12, sm: 6, md: 4 };
  };

  const renderField = (c) => {
    const value = form[c.key] ?? "";
    const setValue = (v) => setForm((prev) => ({ ...prev, [c.key]: v }));

    const displayLabel = c.group ? `${c.group} ${c.header}` : c.header;

    if (c.fieldType === "icon" || c.fieldType === "upload") return null;

    const span = getFieldSpan(c);

    return (
      <Grid item xs={span.xs} sm={span.sm} md={span.md} key={c.key}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {displayLabel}
          </Typography>

          {c.fieldType === "select" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Select
                value={value}
                size="small"
                fullWidth
                onChange={(e) => setValue(e.target.value)}
              >
                {uniqueStrings([
                  ...(Array.isArray(typeOpts[c.optionsKey])
                    ? typeOpts[c.optionsKey]
                    : []),
                  value,
                ]).map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => {
                  setActiveOpt({ optionsKey: c.optionsKey, header: c.header });
                  setAddOptOpen(true);
                }}
                sx={{ p: 0.25 }}
                title="Add option"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <TextField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              size="small"
              fullWidth
              type={c.fieldType === "date" ? "date" : "text"}
              InputLabelProps={
                c.fieldType === "date" ? { shrink: true } : undefined
              }
            />
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {mode === "create"
            ? `Create New - ${selectedType}`
            : `Edit - ${selectedType}`}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAddColOpen(true)}
            >
              Add Column
            </Button>
            <IconButton onClick={onCancel} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {visibleColumns.map(renderField)}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => onSave(form)}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddColumnDialog
        open={addColOpen}
        onClose={() => setAddColOpen(false)}
        onAdd={(header) => onAddColumn(header)}
      />

      <AddOptionDialog
        open={addOptOpen}
        onClose={() => {
          setAddOptOpen(false);
          setActiveOpt(null);
        }}
        columnHeader={activeOpt?.header || ""}
        onAdd={(v) => {
          if (!activeOpt) return;
          onAddOption(activeOpt.optionsKey, v);
        }}
      />
    </>
  );
}

const buildSpecsPayload = (dynamicColumns, values, selectedTypeOptions) => {
  const specs = {};

  dynamicColumns.forEach((column) => {
    const key = column.key;
    const optionsKey = column.optionsKey || key;
    const rawValue = values?.[key];
    const normalizedValue =
      rawValue === null || rawValue === undefined ? "" : rawValue;

    specs[key] = {
      header: column.header,
      value: normalizedValue,
      fieldType: column.fieldType || "select",
      optionsKey,
      options: uniqueStrings([
        ...(selectedTypeOptions?.[optionsKey] || []),
        normalizedValue,
      ]),
    };
  });

  return specs;
};

const buildApiPayloadFromForm = (
  values,
  selectedType,
  dynamicColumns,
  optionsByTypeState,
) => {
  const selectedTypeOptions = optionsByTypeState[selectedType] || {};

  return {
    inspectionTestActivities: values?.inspectionTestActivities ?? "",
    rev: values?.rev ?? "",
    procedureNo: values?.procedureNo ?? "",
    specificInstruction: values?.specificInstruction ?? "",

    controllingDoc: values?.controllingDoc ?? null,
    resPartyLoc: values?.resPartyLoc ?? null,
    acceptanceCriteria: values?.acceptanceCriteria ?? null,
    recordingDoc: values?.recordingDoc ?? null,
    startDate: toNullableDate(values?.startDate),
    endDate: toNullableDate(values?.endDate),
    progress:
      values?.progress === "" || values?.progress === null
        ? null
        : values?.progress,
    actualHours:
      values?.actualHours === "" || values?.actualHours === null
        ? null
        : values?.actualHours,
    link: values?.link ?? null,

    authorizedInspectionDate: toNullableDate(values?.inspectorDate),
    authorizedInspectionFunc: values?.inspectorType ?? "",
    authorizedInspectionInitials: values?.inspectorSign ?? "",

    representativeDate: toNullableDate(values?.shopDate),
    representativeFunc: values?.shopType ?? "",
    representativeInitials: values?.shopSign ?? "",

    cdtDate: toNullableDate(values?.clientDate),
    cdtFunc: values?.clientType ?? "",
    cdtInitials: values?.clientSign ?? "",

    type: selectedType,
    specs: buildSpecsPayload(dynamicColumns, values, selectedTypeOptions),
  };
};

export default function DynamicITPFrontOnly({
  ITP_TYPES = [],
  optionsByType = {},
  allTasks = [],
  setSnackbar,
  refreshItpRows,
}) {
  const {
    data: allItps = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useGetItps();

  const createItpMutation = useCreateItp();
  const updateItpMutation = useUpdateItp();
  const deleteItpMutation = useDeleteItp();

  const [selectedType, setSelectedType] = React.useState("");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalMode, setModalMode] = React.useState("create");
  const [editingRowId, setEditingRowId] = React.useState(null);
  const [rowSelection, setRowSelection] = React.useState({});

  const [optionsByTypeState, setOptionsByTypeState] =
    React.useState(optionsByType);
  const [localDynamicColumnsByType, setLocalDynamicColumnsByType] =
    React.useState({});
  const [fileMetaByRowId, setFileMetaByRowId] = React.useState({});

  const availableTypes = React.useMemo(() => {
    const backendTypes = uniqueStrings(
      (Array.isArray(allItps) ? allItps : [])
        .map((item) => item?.type)
        .filter(Boolean),
    );

    if (backendTypes.length) return backendTypes;

    return uniqueStrings(ITP_TYPES);
  }, [allItps, ITP_TYPES]);

  React.useEffect(() => {
    if (!availableTypes.length) {
      setSelectedType("");
      return;
    }

    setSelectedType((prev) =>
      prev && availableTypes.includes(prev) ? prev : availableTypes[0],
    );
  }, [availableTypes]);

  React.useEffect(() => {
    setRowSelection({});
  }, [selectedType]);

  const selectedTypeApiRows = React.useMemo(() => {
    if (!selectedType) return [];
    return (Array.isArray(allItps) ? allItps : []).filter(
      (item) => item?.type === selectedType,
    );
  }, [allItps, selectedType]);

  const { columns: fetchedDynamicColumns, optionsMap: fetchedOptionsMap } =
    React.useMemo(
      () => buildDynamicArtifactsFromApiRows(selectedTypeApiRows),
      [selectedTypeApiRows],
    );

  const localDynamicColumns = localDynamicColumnsByType[selectedType] || [];

  const dynamicColumns = React.useMemo(
    () => mergeColumns(fetchedDynamicColumns, localDynamicColumns),
    [fetchedDynamicColumns, localDynamicColumns],
  );

  const schemaColumns = React.useMemo(
    () => mergeColumns(NORMALIZED_BASE_SCHEMA, dynamicColumns),
    [dynamicColumns],
  );

  const effectiveOptionsByTypeState = React.useMemo(() => {
    const next = { ...(optionsByTypeState || {}) };

    if (!selectedType) return next;

    const currentTypeOptions = { ...(next[selectedType] || {}) };

    Object.entries(fetchedOptionsMap || {}).forEach(([key, opts]) => {
      currentTypeOptions[key] = uniqueStrings([
        ...(currentTypeOptions[key] || []),
        ...(opts || []),
      ]);
    });

    next[selectedType] = currentTypeOptions;
    return next;
  }, [optionsByTypeState, selectedType, fetchedOptionsMap]);

  const rows = React.useMemo(() => {
    return selectedTypeApiRows.map((row) =>
      mapApiRowToUiRow(row, fileMetaByRowId),
    );
  }, [selectedTypeApiRows, fileMetaByRowId]);

  const selectedRows = React.useMemo(() => {
    return rows.filter((row) => rowSelection[String(row.id)]);
  }, [rows, rowSelection]);

  const onFileSelected = React.useCallback((rowId, columnKey, file) => {
    setFileMetaByRowId((prev) => ({
      ...prev,
      [rowId]: {
        ...(prev[rowId] || {}),
        [columnKey]: {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
        },
      },
    }));
  }, []);

  const mrtColumns = React.useMemo(
    () =>
      buildMrtColumns(
        schemaColumns,
        selectedType,
        effectiveOptionsByTypeState,
        onFileSelected,
      ),
    [schemaColumns, selectedType, effectiveOptionsByTypeState, onFileSelected],
  );

  const { isSendingToInspectionPlan, sendToInspectionPlan } =
    useSendToInspectionPlan({
      allTasks,
      itpRows: rows,
      selectedType,
      setSnackbar,
      refreshItpRows: refreshItpRows || refetch,
    });

  const handleSendToInspectionPlan = async () => {
    await sendToInspectionPlan(selectedRows);
    setRowSelection({});
  };

  const onEditRow = (row) => {
    setModalMode("edit");
    setEditingRowId(row.original.id);
    setModalOpen(true);
  };

  const onDeleteRow = async (row) => {
    try {
      await deleteItpMutation.mutateAsync(row.original.id);
    } catch (err) {
      console.error("Failed to delete ITP row:", err);
    }
  };

  const table = useMaterialReactTable({
    columns: mrtColumns,
    data: rows,
    getRowId: (row) => String(row.id),
    enableEditing: true,
    editDisplayMode: "modal",
    createDisplayMode: "modal",
    enableSorting: false,
    enableColumnFilters: false,

    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },

    muiTablePaperProps: {
      sx: {
        border: "1px solid #cfcfcf",
        borderRadius: 0,
        overflow: "hidden",
      },
    },

    muiTableProps: {
      sx: {
        tableLayout: "auto",
        borderCollapse: "collapse",
      },
    },

    muiTableHeadCellProps: {
      sx: {
        px: 1,
        py: 0.75,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: "nowrap",
        border: "1px solid #cfcfcf",
        backgroundColor: "#f5f5f5",
        textAlign: "center",
        "& .Mui-TableSortLabel-root": { display: "none" },
      },
    },

    muiTableBodyCellProps: {
      sx: {
        px: 1,
        py: 0.75,
        fontSize: 12,
        border: "1px solid #cfcfcf",
        verticalAlign: "top",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },

    muiTableBodyRowProps: {
      sx: {
        "&:hover td": {
          backgroundColor: "#fafafa",
        },
      },
    },

    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!selectedType}
          onClick={() => {
            setModalMode("create");
            setEditingRowId(null);
            setModalOpen(true);
          }}
        >
          Add {selectedType || "ITP"}
        </Button>

        <Button
          variant="contained"
          color="success"
          disabled={
            !selectedType ||
            selectedRows.length === 0 ||
            isSendingToInspectionPlan
          }
          onClick={handleSendToInspectionPlan}
        >
          {isSendingToInspectionPlan
            ? "Sending..."
            : "Send To Inspection Plan"}
        </Button>
      </Box>
    ),

    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => {
              onEditRow(row);
            }}
            sx={{ p: 0.25 }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={() => {
              onDeleteRow(row);
            }}
            sx={{ p: 0.25 }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    enableRowActions: true,
  });

  const initialValues = React.useMemo(() => {
    if (modalMode !== "edit" || !editingRowId) return {};
    const found = rows.find((r) => String(r.id) === String(editingRowId));
    return found || {};
  }, [modalMode, editingRowId, rows]);

  const handleAddColumn = (header) => {
    if (!selectedType) return;

    const key = slugifyKey(header);
    const optionsKey = key;

    setLocalDynamicColumnsByType((prev) => {
      const current = Array.isArray(prev[selectedType])
        ? prev[selectedType]
        : [];

      if (current.some((col) => col.key === key)) {
        return prev;
      }

      return {
        ...prev,
        [selectedType]: [
          ...current,
          {
            key,
            header,
            fieldType: "select",
            optionsKey,
            size: 160,
          },
        ],
      };
    });

    setOptionsByTypeState((prev) => {
      const next = { ...(prev || {}) };
      const typeOptions = { ...(next[selectedType] || {}) };

      if (!Array.isArray(typeOptions[optionsKey])) {
        typeOptions[optionsKey] = [];
      }

      next[selectedType] = typeOptions;
      return next;
    });
  };

  const handleAddOption = (optionsKey, value) => {
    if (!selectedType) return;

    setOptionsByTypeState((prev) => {
      const next = { ...(prev || {}) };
      const obj = { ...(next[selectedType] || {}) };
      const list = Array.isArray(obj[optionsKey]) ? [...obj[optionsKey]] : [];
      if (!list.includes(value)) list.push(value);
      obj[optionsKey] = list;
      next[selectedType] = obj;
      return next;
    });
  };

  const handleSaveRow = async (values) => {
    if (!selectedType) return;

    const payload = buildApiPayloadFromForm(
      values,
      selectedType,
      dynamicColumns,
      effectiveOptionsByTypeState,
    );

    try {
      if (modalMode === "edit" && editingRowId) {
        await updateItpMutation.mutateAsync({
          values: {
            id: editingRowId,
            ...payload,
          },
        });
      } else {
        await createItpMutation.mutateAsync({
          type: selectedType,
          values: payload,
        });
      }

      setModalOpen(false);
    } catch (err) {
      console.error("Failed to save ITP row:", err);
    }
  };

  const isSaving = createItpMutation.isPending || updateItpMutation.isPending;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <CircularProgress size={22} />
        <Typography>Loading ITP data...</Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error">
          Failed to load ITP data
          {error?.message ? `: ${error.message}` : "."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: 700 }}>ITP Type:</Typography>
        <Select
          size="small"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          sx={{ minWidth: 360 }}
          displayEmpty
        >
          {availableTypes.map((t) => (
            <MenuItem key={t} value={t}>
              {t}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <MaterialReactTable table={table} />

      <ITPRowModal
        open={modalOpen}
        mode={modalMode}
        schemaColumns={schemaColumns}
        selectedType={selectedType}
        optionsByTypeState={effectiveOptionsByTypeState}
        onAddColumn={handleAddColumn}
        onAddOption={handleAddOption}
        initialValues={initialValues}
        onCancel={() => setModalOpen(false)}
        onSave={handleSaveRow}
        isSaving={isSaving}
      />
    </Box>
  );
}